import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import _ from 'lodash'
import * as blueprint from '@adc/blueprint'
import {
  trackConversationalFormSkip,
  trackConversationalFormStepLoad,
} from 'common/services/analytics'
import { ProductLine, useApplicationTheme, type Theme } from '@adc/ui-adc'
import configment from '@adc/configment'
import { StepId } from './types'
import { validateCurrent } from './utils'
import { sellerFlowPath, sellerSteps, Step } from '.'

interface ContextProps {
  data: {
    isLoading: boolean
    stepProgression: any
    leadIntake: Record<string, string>
    validations: any
    listingId: string
    currentStep: Step
    progress: any
  }
  actions: any
}
export interface LeadIntake {
  'property.property_type'?: string
  'property.bedrooms'?: string
  'property.bathrooms'?: string
  'property.home_square_footage'?: string
  [key: string]: unknown
}

export interface StepProgressionType {
  isNextDisabled: boolean
  isCurrentlyValidating: boolean
}

type ValidationStore = Record<string, string>

const Context = createContext<ContextProps | undefined>(undefined)

export const redirectToListingView = (
  listingId: string,
  theme: Theme,
  includeNewListingParam?: boolean
) => {
  const targetPath = `/dashboard/seller/listings/${listingId}/details${
    includeNewListingParam ? '?formCompleted=true' : ''
  }`
  const auctioncomHost = configment.get('AUCTIONCOM_HOST')
  const alreadyOnAuctioncom =
    window.location.origin === auctioncomHost ||
    theme.productLine === ProductLine.AuctionCom

  if (alreadyOnAuctioncom) {
    window.location.replace(targetPath)
  } else {
    const redirectUrl = `${auctioncomHost}${targetPath}`
    const targetUrl = `${
      window.location.origin
    }/apps/api/session-handoff?redirect-to=${encodeURIComponent(redirectUrl)}`
    window.location.replace(targetUrl)
  }
}

// Context type for useFlow
interface FlowContext {
  featureFlags: Record<string, any>
  leadIntake: Record<string, string>
}

/**
 * Build a FlowDefinition from the existing sellerSteps Map.
 * Starts from the given initialStepId by placing it first in the array.
 */
const buildFlowDefinition = (
  featureFlags: Record<string, any>,
  initialStepId: StepId,
  stepsMap: Map<StepId, Step>
) => {
  const steps: any[] = []
  const stepsAfterInitial: any[] = []
  let foundInitial = false

  stepsMap.forEach((step, stepId) => {
    const flowStep = {
      id: stepId,
      next: ({ context }: { context: FlowContext }) => {
        const answer = context?.leadIntake || {}
        const nextStepId = step.next({ answer, featureFlags })

        // Skip disabled steps
        let actualNextStepId = nextStepId
        let actualNextStep = stepsMap.get(actualNextStepId)
        while (actualNextStep?.disabled && actualNextStep.disabled(answer)) {
          actualNextStepId = actualNextStep.next({ answer, featureFlags })
          actualNextStep = stepsMap.get(actualNextStepId)
        }

        return actualNextStepId
      },
      isLastStep: ({ context }: { context: FlowContext }) => {
        const answer = context?.leadIntake || {}
        return step.isLastStep ? step.isLastStep(answer) : false
      },
      canBeSkipped: ({ context }: { context: FlowContext }) => {
        const answer = context?.leadIntake || {}
        return step.disabled ? step.disabled(answer) : false
      },
    }

    if (stepId === initialStepId) {
      foundInitial = true
    }

    if (foundInitial) {
      steps.push(flowStep)
    } else {
      stepsAfterInitial.push(flowStep)
    }
  })

  // Put steps before initial at the end (for retreat to work)
  return [...steps, ...stepsAfterInitial]
}

const StepManagerProvider = ({
  children,
  initialData,
  history,
  stepsMap = sellerSteps,
}: {
  children: ReactNode
  initialData: {
    initialLeadIntake: any
    featureFlags: any
    initialStep: Step
    initialLeadValidations: ValidationStore
  }
  history?: any
  stepsMap?: Map<StepId, Step>
}) => {
  const [listingId, setListingId] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [leadIntake, setLeadIntake] = useState<Record<string, string>>(
    initialData.initialLeadIntake
  )
  const [stepProgression, setStepProgression] = useState<StepProgressionType>({
    isNextDisabled: false,
    isCurrentlyValidating: false,
  })
  const [validations, setValidations] = useState<ValidationStore>(
    initialData?.initialLeadValidations
  )
  const theme = useApplicationTheme()

  // Build flow definition from stepsMap, starting at initialStep
  const flowDefinition = useMemo(
    () =>
      buildFlowDefinition(
        initialData.featureFlags,
        initialData.initialStep.id,
        stepsMap
      ),
    [initialData.featureFlags, initialData.initialStep.id, stepsMap]
  )

  // Use a ref for leadIntake so useFlow's context always has the latest value
  const leadIntakeRef = useRef(leadIntake)
  leadIntakeRef.current = leadIntake

  // Flow context — uses ref to always provide fresh leadIntake to step functions
  const flowContext = useMemo<FlowContext>(
    () => ({
      featureFlags: initialData.featureFlags,
      get leadIntake() {
        return leadIntakeRef.current
      },
    }),
    [initialData.featureFlags]
  )

  // Analytics emitter for useFlow
  const emitter = useMemo(
    () => ({
      onStep: (stepId: StepId) => {
        const step = stepsMap.get(stepId)
        if (step?.ga?.virtualTitle && step?.ga?.virtualUrl) {
          const testInfo =
            initialData?.featureFlags[
              'fe.resi.ab.testing.on.conversational.flow.step.order'
            ]?.GA || {}
          trackConversationalFormStepLoad({
            ...step.ga,
            ...testInfo,
          })
        }
      },
    }),
    [initialData.featureFlags]
  )

  // useFlow hook from blueprint
  const { data: flowData, actions: flowActions } =
    blueprint.flows.hooks.useFlow<StepId, Record<string, string>, FlowContext>({
      definition: flowDefinition,
      context: flowContext,
      emitter,
      initialData: leadIntake,
    })

  // Map useFlow's currentStepObject back to the original Step type
  const currentStep = useMemo(
    () => stepsMap.get(flowData.currentStepObject.id) as Step,
    [flowData.currentStepObject.id, stepsMap]
  )

  // Progress as percentage (0-100).
  // Blueprint's advance never assigns full remaining progress because its
  // isLastStep check that gates the full increment also prevents advancing.
  // Compensate: if we're on the terminal step, force progress to 100.
  const isOnLastStep = currentStep.isLastStep
    ? currentStep.isLastStep(leadIntake)
    : false
  const progress = isOnLastStep ? 100 : flowData.currentProgress

  const onPreviousStep = (e: Event) => {
    e?.preventDefault?.()
    flowActions.retreat()
  }

  const onNextStep = async (
    e: Event,
    listingId?: string,
    needsAuthentication?: boolean
  ) => {
    e?.preventDefault?.()
    const updatedLeadIntake = _.cloneDeep(leadIntake)
    let hasErrors = false
    if (stepProgression.isNextDisabled) {
      setStepProgression((prev: StepProgressionType) => ({
        ...prev,
        isCurrentlyValidating: true,
      }))

      const validationResult = await validateCurrent(
        updatedLeadIntake,
        currentStep.questions
      )

      hasErrors = validationResult.hasErrors

      setStepProgression((progression: StepProgressionType) => ({
        ...progression,
        isNextDisabled: hasErrors,
        isCurrentlyValidating: false,
      }))

      setValidations(prev => ({ ...prev, ...validationResult.nextValidations }))

      if (hasErrors) return
    }

    if (
      currentStep.isSubmitStep?.(updatedLeadIntake) &&
      listingId &&
      !needsAuthentication
    ) {
      // Go to listing flow
      redirectToListingView(listingId, theme, true)
    } else {
      // Advance the flow — useFlow handles step navigation internally
      flowActions.advance({
        data: updatedLeadIntake,
        context: flowContext,
      })
    }
  }

  const onSkipAuthStep = (e: Event) => {
    trackConversationalFormSkip()
    onNextStep(e)
  }

  // URL sync — push step path to history when step changes
  useEffect(() => {
    const unlisten = history
      ? history.listen((_location: string, action: string) => {
          if (action === 'POP') {
            history.push(`${sellerFlowPath}${location.search}`)
            location.href = `${sellerFlowPath}${location.search}`
          }
        })
      : () => {}

    return () => {
      unlisten()
    }
  }, [history])

  useEffect(() => {
    if (history) {
      const currentQueryParams = location.search
      if (currentStep.id === initialData.initialStep.id) {
        history.push(`${sellerFlowPath}${currentQueryParams}`)
      } else {
        history.push(`${currentStep.path}${currentQueryParams}`)
      }
    }

    const run = async () => {
      const { hasErrors } = await validateCurrent(
        leadIntake,
        currentStep.questions
      )
      setStepProgression(prev => ({ ...prev, isNextDisabled: hasErrors }))
    }

    run()
  }, [currentStep])

  const data = {
    isLoading,
    stepProgression,
    leadIntake,
    validations,
    listingId,
    currentStep,
    progress,
  }
  const actions = {
    setIsLoading,
    setStepProgression,
    setLeadIntake,
    onPreviousStep,
    onNextStep,
    onSkipAuthStep,
    setListingId,
    setValidations,
  }

  return (
    <Context.Provider value={{ data, actions }}>{children}</Context.Provider>
  )
}

const useStepManagerContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error('useStepManagerContext must be used within its Provider')
  }
  return context
}

export { StepManagerProvider, useStepManagerContext }
